import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import FloatingSupportChat from "../components/FloatingSupportChat";
import api from "../utils/api";
import { completeEnquiry } from "../utils/enquiryConfirmation";
import { trackFunnelEvent, trackFunnelExit } from "../utils/funnelTracking";

const MIN_NOTICE_HOURS = 24.5;
const ENQUIRY_MIN_HOURS = 4.5;
const CCG_LONG_DISTANCE_KM = 80;
const CCG_HIGH_FARE_GBP = 120;

const brandByPath = (pathname = "") => {
  if (pathname.includes("/coach-book")) {
    return {
      siteMode: "cheapcoachgrab",
      brandName: "CheapCoachGrab",
      platformName: "CheapCabGrab",
      titleSuffix: "coach",
      bookingIntro: "Secure coach booking handled by CheapCabGrab.",
      enquiryIntro: "Coach confirmation is handled by the CheapCabGrab booking team.",
      successTitle: "We have your coach enquiry",
      supportLine: "Coach allocation, luggage and pickup details are checked before confirmation.",
    };
  }
  if (pathname.includes("/minibus-book")) {
    return {
      siteMode: "minibusgrab",
      brandName: "MinibusGrab",
      platformName: "CheapCabGrab",
      titleSuffix: "minibus",
      bookingIntro: "Secure minibus booking handled by CheapCabGrab.",
      enquiryIntro: "Minibus confirmation is handled by the CheapCabGrab booking team.",
      successTitle: "We have your minibus enquiry",
      supportLine: "Flights, traffic and airport timing are checked around your group.",
    };
  }
  return {
    siteMode: "ccg",
    brandName: "CheapCabGrab",
    platformName: "CheapCabGrab",
    titleSuffix: "taxi",
    bookingIntro: "Secure taxi booking handled by CheapCabGrab.",
    enquiryIntro: "Confirmation required — no payment taken now.",
    successTitle: "We have your enquiry",
    supportLine: "Fixed fares, flight monitoring and support are handled by CheapCabGrab.",
  };
};

const isPublicFareVisible = ({ brand, quote, selected, requiresEnquiry }) => {
  if (!requiresEnquiry) return true;
  if (brand.siteMode === "minibusgrab" || brand.siteMode === "cheapcoachgrab") return false;
  if (selected?.partner_fare_available === true) return true;
  if (selected?.partner_fare_available == null) return true;

  const distanceKm = Number(quote?.distance_km);
  const referenceCard = quote?.vehicles?.find((vehicle) => vehicle.code === "saloon" && vehicle.available !== false);
  const referenceFare = Number(referenceCard?.price ?? selected?.price);
  return !(
    Number.isFinite(distanceKm) && distanceKm >= CCG_LONG_DISTANCE_KM &&
    Number.isFinite(referenceFare) && referenceFare > CCG_HIGH_FARE_GBP
  );
};

const vehicleDetails = {
  saloon: {
    title: "Saloon Car",
    subtitle: "Best for airport transfers, solo travellers and couples",
    image: "/ccg-hero.png",
    badge: "Instant booking",
    passengers: "Up to 4 Passengers",
    bags: "2 Bags",
  },
  mpv: {
    title: "MPV",
    subtitle: "Extra space for families, luggage and airport journeys",
    image: "/ccg-mpv.png",
    badge: "Instant booking",
    passengers: "Up to 6 Passengers",
    bags: "4 Bags",
  },
  nine: {
    title: "8 Seater Minibus",
    subtitle: "Perfect for small groups and families",
    image: "/minibus-hero.png",
    badge: "Instant booking",
    passengers: "8 Passengers",
    bags: "8 Large bags",
  },
  fourteen: {
    title: "16 Seater Coach",
    subtitle: "Spacious and comfortable for bigger groups",
    image: "/coach-hero.png",
    badge: "Confirmation required",
    passengers: "16 Passengers",
    bags: "16 Large bags",
  },
  twentyfive: {
    title: "25 Seater Coach",
    subtitle: "Coordinated travel for larger airport groups",
    image: "/coach-hero.png",
    badge: "Coach partner check",
    passengers: "25+ Passengers",
    bags: "Group luggage",
  },
};

const premiumVehicleLimits = {
  saloon: { pax: 4, bags: 2 },
  mpv: { pax: 6, bags: 4 },
  nine: { pax: 8, bags: 8 },
  fourteen: { pax: 16, bags: 16 },
  twentyfive: { pax: 25, bags: 20 },
};

const ccgVehicleLimits = {
  saloon: { pax: 4, bags: 2 },
  mpv: { pax: 6, bags: 4 },
  nine: { pax: 8, bags: 6 },
  fourteen: { pax: 14, bags: 12 },
  twentyfive: { pax: 25, bags: 20 },
};

const extraPrices = {
  travelpak_bundle: 24.99,
  sim_o2: 11.99,
  adapter: 13.99,
  meet_greet: 10.99,
};

const extraLabels = {
  travelpak_bundle: "TravelPak bundle",
  sim_o2: "SIM (O2 UK)",
  adapter: "Universal adapter",
  meet_greet: "Meet & greet",
};

const money = (value) => {
  const amount = Number(value || 0);
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

const toInputDateTime = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

const minPickupIso = (hoursAhead) => {
  const d = new Date(Date.now() + hoursAhead * 60 * 60 * 1000);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

const displayDate = (value) => {
  if (!value) return "Not set";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

const displayTime = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
};

const isValidEmail = (email = "") => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(email).trim());
const isValidPhone = (phone = "") => {
  const digits = String(phone).replace(/\D/g, "");
  return digits.length >= 7 && digits.length <= 17;
};
const isValidName = (name = "") => String(name).trim().split(/\s+/).filter(Boolean).length >= 2;

const PremiumBooking = () => {
  const { quoteId, token, vehicleCode } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const brand = useMemo(() => brandByPath(location.pathname), [location.pathname]);

  const [quote, setQuote] = useState(null);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [formError, setFormError] = useState("");
  const [successDetails, setSuccessDetails] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [discountCode, setDiscountCode] = useState("");
  const [extras, setExtras] = useState({
    travelpak_bundle: false,
    sim_o2: false,
    adapter: false,
    meet_greet: false,
  });
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    passengers: "",
    bags: "",
    pickup_time: "",
    flight_number: "",
    notes: "",
  });
  const formStartedRef = useRef(false);
  const reachedFieldsRef = useRef(new Set());
  const completedFieldsRef = useRef(new Set());
  const lastFieldRef = useRef(null);
  const contactSectionRef = useRef(null);
  const contactSectionSeenRef = useRef(false);
  const submissionCompletedRef = useRef(false);

  useEffect(() => {
    const fetchQuote = async () => {
      if (!quoteId || !token) {
        setError("Quote details missing. Please start a new quote.");
        setLoading(false);
        return;
      }
      setLoading(true);
      setError("");
      try {
        const res = await api.get(`/api/quotes-v2/${quoteId}/${token}`);
        if (!res.data?.vehicles) {
          setError(res.data?.error || "Unable to load quote.");
          return;
        }
        const match = res.data.vehicles.find((vehicle) => vehicle.code === vehicleCode);
        if (!match) {
          setError("Selected vehicle is no longer available.");
          return;
        }
        const enquiryMode =
          res.data.eligibility?.enquiry_only || match.book_mode === "enquiry" || !match.available;
        setQuote(res.data);
        setSelected(match);
        const handoff = new URLSearchParams(location.search || "");
        setForm((prev) => ({
          ...prev,
          passengers: res.data.request?.passengers || "",
          bags: res.data.request?.bags ?? "",
          pickup_time:
            toInputDateTime(res.data.request?.pickup_time) ||
            minPickupIso(enquiryMode ? ENQUIRY_MIN_HOURS : MIN_NOTICE_HOURS),
          flight_number: handoff.get("flight_number") || prev.flight_number,
          notes: handoff.get("ajp_journey_id") && !String(prev.notes || "").includes(handoff.get("ajp_journey_id"))
            ? [prev.notes, `Airport Journey Planner reference: ${handoff.get("ajp_journey_id")}`].filter(Boolean).join("\n")
            : prev.notes,
        }));
      } catch (err) {
        setError(err.response?.data?.error || "Unable to load quote.");
      } finally {
        setLoading(false);
      }
    };
    fetchQuote();
  }, [quoteId, token, vehicleCode, location.search]);

  const requiresEnquiry =
    quote?.eligibility?.enquiry_only || selected?.book_mode === "enquiry" || !selected?.available;
  const vehicleLimits = brand.siteMode === "ccg" ? ccgVehicleLimits : premiumVehicleLimits;
  const limits = vehicleLimits[selected?.code] || {};
  const details = vehicleDetails[selected?.code] || {
    title: selected?.label || "Selected vehicle",
    subtitle: "Private transfer for your journey",
    image: brand.siteMode === "ccg" ? "/ccg-hero.png" : "/minibus-hero.png",
    badge: requiresEnquiry ? "Confirmation required" : "Instant booking",
    passengers: `${limits.pax || "Group"} Passengers`,
    bags: `${limits.bags || "Group"} Bags`,
  };

  const minimumPickup = useMemo(
    () => minPickupIso(requiresEnquiry ? ENQUIRY_MIN_HOURS : MIN_NOTICE_HOURS),
    [requiresEnquiry]
  );

  useEffect(() => {
    setForm((prev) => {
      const normalized = toInputDateTime(prev.pickup_time);
      if (!normalized || normalized < minimumPickup) {
        return { ...prev, pickup_time: minimumPickup };
      }
      return { ...prev, pickup_time: normalized };
    });
  }, [minimumPickup]);

  const extrasTotal = useMemo(() => {
    if (extras.travelpak_bundle) {
      return extraPrices.travelpak_bundle + (extras.meet_greet ? extraPrices.meet_greet : 0);
    }
    return Object.entries(extras).reduce((total, [key, enabled]) => {
      if (!enabled) return total;
      return total + (extraPrices[key] || 0);
    }, 0);
  }, [extras]);

  const baseFare = Number(selected?.price || 0);
  const estimatedTotal = Math.round((baseFare + extrasTotal) * 100) / 100;
  const publicFareVisible = isPublicFareVisible({ brand, quote, selected, requiresEnquiry });
  const ctaLabel = requiresEnquiry ? "Send enquiry" : `Pay now ${money(estimatedTotal)}`;

  useEffect(() => {
    if (!quote?.quote_id || !selected?.code) return;
    trackFunnelEvent("booking_form_view", {
      brand: brand.siteMode, quote_id: quote.quote_id, vehicle_code: selected.code,
      fare: publicFareVisible ? Number(selected.price || 0) : undefined,
      mode: requiresEnquiry ? "enquiry" : "booking",
      public_price_visible: publicFareVisible,
    });
  }, [brand.siteMode, publicFareVisible, quote?.quote_id, requiresEnquiry, selected?.code, selected?.price]);

  useEffect(() => {
    if (!quote?.quote_id || !contactSectionRef.current || contactSectionSeenRef.current || typeof IntersectionObserver === "undefined") return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.5)) return;
      contactSectionSeenRef.current = true;
      trackFunnelEvent("contact_section_view", {
        brand: brand.siteMode, quote_id: quote.quote_id, vehicle_code: selected?.code,
      });
      observer.disconnect();
    }, { threshold: [0.5] });
    observer.observe(contactSectionRef.current);
    return () => observer.disconnect();
  }, [brand.siteMode, quote?.quote_id, requiresEnquiry, selected?.code]);

  useEffect(() => {
    if (!quote?.quote_id || !requiresEnquiry) return undefined;
    const recordAbandon = () => {
      if (submissionCompletedRef.current) return;
      trackFunnelExit("enquiry_flow_abandon", {
        brand: brand.siteMode, quote_id: quote.quote_id, vehicle_code: selected?.code,
        step: lastFieldRef.current || "form_view", status: "page_exit",
      });
    };
    window.addEventListener("pagehide", recordAbandon);
    return () => window.removeEventListener("pagehide", recordAbandon);
  }, [brand.siteMode, quote?.quote_id, selected?.code]);

  const trackFieldReached = (fieldName) => {
    lastFieldRef.current = fieldName;
    if (!formStartedRef.current) {
      formStartedRef.current = true;
      trackFunnelEvent("booking_form_start", {
        brand: brand.siteMode, quote_id: quote?.quote_id, vehicle_code: selected?.code, first_field: fieldName,
      });
    }
    if (reachedFieldsRef.current.has(fieldName)) return;
    reachedFieldsRef.current.add(fieldName);
    trackFunnelEvent("booking_field_reached", {
      brand: brand.siteMode, quote_id: quote?.quote_id, vehicle_code: selected?.code, field_name: fieldName,
    });
  };

  const trackFieldCompleted = (fieldName, value) => {
    if (!String(value ?? "").trim() || completedFieldsRef.current.has(fieldName)) return;
    completedFieldsRef.current.add(fieldName);
    trackFunnelEvent("booking_field_complete", {
      brand: brand.siteMode, quote_id: quote?.quote_id, vehicle_code: selected?.code,
      field_name: fieldName, field_state: "non_empty",
    });
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormError("");
    trackFieldReached(name);
    if (name === "pickup_time") {
      setForm((prev) => ({ ...prev, pickup_time: value && value < minimumPickup ? minimumPickup : value }));
      return;
    }
    if (name === "passengers" || name === "bags") {
      const max = name === "passengers" ? limits.pax : limits.bags;
      const next = Math.max(0, Number(value || 0));
      setForm((prev) => ({ ...prev, [name]: max ? Math.min(next, max) : next }));
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const toggleExtra = (key) => {
    setExtras((prev) => {
      if (key === "travelpak_bundle") {
        const bundleOn = !prev.travelpak_bundle;
        return { ...prev, travelpak_bundle: bundleOn, sim_o2: bundleOn ? false : prev.sim_o2, adapter: bundleOn ? false : prev.adapter };
      }
      if ((key === "sim_o2" || key === "adapter") && prev.travelpak_bundle) {
        return { ...prev, travelpak_bundle: false, [key]: !prev[key] };
      }
      return { ...prev, [key]: !prev[key] };
    });
  };

  const validate = () => {
    if (!isValidName(form.name)) return "Please enter your full name.";
    if (!isValidEmail(form.email)) return "Please enter a valid email address.";
    if (!isValidPhone(form.phone)) return "Please enter a valid phone number.";
    if (!form.pickup_time) return "Please choose a pickup time.";
    if (Number(form.passengers || 0) < 1) return "Please enter the passenger count.";
    if (limits.pax && Number(form.passengers) > limits.pax) return `Please keep passengers within ${details.title} capacity.`;
    if (limits.bags && Number(form.bags) > limits.bags) return `Please keep bags within ${details.title} capacity.`;
    return "";
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setFormError("");
    const validationError = validate();
    if (validationError) {
      trackFunnelEvent("booking_validation_error", {
        brand: brand.siteMode, quote_id: quote?.quote_id, vehicle_code: selected?.code,
        step: lastFieldRef.current || "submit", error_type: validationError,
      });
      setFormError(validationError);
      return;
    }
    setSubmitting(true);
    trackFunnelEvent("booking_submit_click", {
      brand: brand.siteMode, quote_id: quote?.quote_id, vehicle_code: selected?.code,
      fare: estimatedTotal, mode: requiresEnquiry ? "enquiry" : "booking",
    });
    const payload = {
      quote_id: quoteId,
      access_token: token,
      vehicle_code: selected.code,
      name: form.name,
      email: form.email,
      phone: form.phone,
      passengers: form.passengers,
      bags: form.bags,
      pickup_time: form.pickup_time,
      flight_number: form.flight_number,
      notes: form.notes,
      extras,
      discount_code: discountCode || undefined,
      brand: brand.siteMode,
      frontend_origin: typeof window !== "undefined" ? window.location.origin : undefined,
    };

    try {
      if (requiresEnquiry) {
        const res = await api.post("/api/enquiries-v2", payload);
        const confirmation = {
          refCode: res.data?.ref_code || "ENQ",
          brand: brand.brandName,
          pickup: quote.request?.pickup,
          dropoff: quote.request?.dropoff,
          passengers: form.passengers,
          bags: form.bags,
          pickupTime: form.pickup_time,
          vehicle: details.title,
        };
        setSuccessDetails({ ...confirmation, title: brand.successTitle });
        submissionCompletedRef.current = true;
        trackFunnelExit("enquiry_complete", {
          brand: brand.siteMode, quote_id: quote?.quote_id, vehicle_code: selected?.code, status: "ok",
        });
        completeEnquiry(navigate, confirmation);
        setSubmitting(false);
        return;
      }
      const res = await api.post("/api/bookings-v2", payload);
      if (res.data?.paymentLink) {
        window.location.href = res.data.paymentLink;
        return;
      }
      setSuccessDetails({
        refCode: res.data?.ref_code || "BK",
        title: "Booking created",
        vehicle: details.title,
      });
    } catch (err) {
      setError(err.response?.data?.error || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleBackToOptions = () => {
    if (requiresEnquiry && !submissionCompletedRef.current) {
      submissionCompletedRef.current = true;
      trackFunnelEvent("enquiry_flow_abandon", {
        brand: brand.siteMode,
        quote_id: quote?.quote_id,
        vehicle_code: selected?.code,
        step: lastFieldRef.current || "form_view",
        status: "back_to_options",
      });
    }
    navigate(-1);
  };

  if (loading) {
    return (
      <main className="premium-booking-page">
        <style>{premiumBookingCss}</style>
        <div className="premium-booking-loading">Loading secure booking...</div>
      </main>
    );
  }

  if (error && !quote) {
    return (
      <main className="premium-booking-page">
        <style>{premiumBookingCss}</style>
        <section className="premium-booking-error">
          <h1>Booking unavailable</h1>
          <p>{error}</p>
          <button type="button" onClick={() => navigate("/")}>Start new quote</button>
        </section>
      </main>
    );
  }

  if (successDetails) {
    return (
      <main className="premium-booking-page">
        <style>{premiumBookingCss}</style>
        <section className="premium-booking-success">
          <div className="success-mark">✓</div>
          <p>{brand.brandName}</p>
          <h1>{successDetails.title}</h1>
          <span>Reference: {successDetails.refCode}</span>
          <strong>{successDetails.vehicle}</strong>
          <small>{brand.supportLine}</small>
          <button type="button" onClick={() => navigate("/")}>Start new quote</button>
        </section>
      </main>
    );
  }

  return (
    <main className={`premium-booking-page premium-booking-${brand.siteMode}`}>
      <style>{premiumBookingCss}</style>
      <FloatingSupportChat defaultFlow={requiresEnquiry ? "enquiry" : "existing_booking"} position="right" showAfterMs={20000} />

      <section className="premium-booking-shell">
        <header className="premium-booking-progress" aria-label="Booking progress">
          {["Journey details", "Your details", "Extras", "Review & pay", "Confirmation"].map((step, index) => (
            <span key={step} className={index === 0 ? "active" : ""}>
              <b>{index + 1}</b>
              {step}
            </span>
          ))}
          <div className="secure-booking">
            <b>▣</b>
            <span>
              <strong>Secure booking</strong>
              <small>Your data is safe with us</small>
            </span>
          </div>
        </header>

        <div className="premium-booking-heading">
          <p>{brand.brandName}</p>
          <h1>{brand.siteMode === "ccg" && requiresEnquiry ? `Complete your ${details.title} enquiry` : `Confirm your ${details.title} ${requiresEnquiry ? "enquiry" : "booking"}`}</h1>
          <strong>{quote.request?.pickup} → {quote.request?.dropoff}</strong>
          <div className="premium-booking-pills">
            <span>▣ Secure payment</span>
            <span>◷ Free 60-min waiting time</span>
            <span>♢ No hidden charges</span>
            <span>☎ 24/7 support</span>
          </div>
        </div>

        <div className="premium-booking-grid">
          <form className="premium-booking-card premium-booking-form" onSubmit={handleSubmit}>
            <section>
              <h2>Journey details</h2>
              <div className="booking-field-grid">
                <label>
                  <span>Pick-up date & time</span>
                  <input name="pickup_time" type="datetime-local" value={form.pickup_time} min={minimumPickup} onFocus={() => trackFieldReached("pickup_time")} onBlur={(event) => trackFieldCompleted("pickup_time", event.target.value)} onChange={handleChange} />
                </label>
                <label>
                  <span>Flight number optional</span>
                  <input name="flight_number" placeholder="e.g. BA123" value={form.flight_number} onFocus={() => trackFieldReached("flight_number")} onBlur={(event) => trackFieldCompleted("flight_number", event.target.value)} onChange={handleChange} />
                </label>
                <label>
                  <span>Number of passengers</span>
                  <input name="passengers" type="number" min="1" max={limits.pax || undefined} value={form.passengers} onFocus={() => trackFieldReached("passengers")} onBlur={(event) => trackFieldCompleted("passengers", event.target.value)} onChange={handleChange} />
                </label>
                <label>
                  <span>Large bags</span>
                  <input name="bags" type="number" min="0" max={limits.bags || undefined} value={form.bags} onFocus={() => trackFieldReached("bags")} onBlur={(event) => trackFieldCompleted("bags", event.target.value)} onChange={handleChange} />
                </label>
              </div>
              <label className="wide-field">
                <span>Notes optional</span>
                <textarea name="notes" placeholder="Child seats, luggage notes, discount request or pickup instructions..." value={form.notes} onFocus={() => trackFieldReached("notes")} onBlur={(event) => trackFieldCompleted("notes", event.target.value)} onChange={handleChange} />
              </label>
            </section>

            <section ref={contactSectionRef}>
              <h2>Your details</h2>
              <div className="booking-field-grid">
                <label>
                  <span>Full name</span>
                  <input name="name" placeholder="Full name" value={form.name} onFocus={() => trackFieldReached("name")} onBlur={(event) => trackFieldCompleted("name", event.target.value)} onChange={handleChange} autoComplete="name" />
                </label>
                <label>
                  <span>Email</span>
                  <input name="email" type="email" placeholder="you@example.com" value={form.email} onFocus={() => trackFieldReached("email")} onBlur={(event) => trackFieldCompleted("email", event.target.value)} onChange={handleChange} autoComplete="email" />
                </label>
                <label>
                  <span>Phone</span>
                  <input name="phone" placeholder="Mobile number" value={form.phone} onFocus={() => trackFieldReached("phone")} onBlur={(event) => trackFieldCompleted("phone", event.target.value)} onChange={handleChange} autoComplete="tel" />
                </label>
              </div>
            </section>

            {brand.siteMode === "ccg" && requiresEnquiry ? (
              <div className="premium-booking-mobile-submit">
                <strong>{publicFareVisible ? `${money(estimatedTotal)} estimated fare` : "Personalised fare check"}</strong>
                <small>No payment is taken when you send this enquiry.</small>
                <button className="premium-booking-submit" type="submit" disabled={submitting}>
                  {submitting ? "Submitting..." : "Send enquiry"} ▣
                </button>
              </div>
            ) : null}

            <section>
              <h2>Extras</h2>
              <div className="premium-extra-grid">
                {Object.keys(extraPrices).map((key) => {
                  const disabled = extras.travelpak_bundle && (key === "sim_o2" || key === "adapter");
                  return (
                    <label key={key} className={disabled ? "disabled" : ""}>
                      <input type="checkbox" checked={extras[key]} disabled={disabled} onChange={() => toggleExtra(key)} />
                      <span>
                        <strong>{extraLabels[key]}</strong>
                        <small>{money(extraPrices[key])}</small>
                      </span>
                    </label>
                  );
                })}
              </div>
              <div className="discount-row">
                <input placeholder="Discount code" value={discountCode} onChange={(event) => setDiscountCode(event.target.value)} />
                <button type="button">Apply</button>
              </div>
            </section>

            {(formError || error) && <div className="premium-booking-message">{formError || error}</div>}
            <div className="no-charge-note">
              <b>♢</b>
              <span>
                <strong>{requiresEnquiry ? "No payment taken now" : "You won't be charged yet"}</strong>
                <small>{requiresEnquiry ? brand.enquiryIntro : "You can review everything before secure payment."}</small>
              </span>
            </div>
          </form>

          <aside className="premium-booking-summary">
            <div className="summary-title">Booking summary</div>
            <div className="summary-body">
              <div className="summary-vehicle">
                <div>
                  <h2>{details.title}</h2>
                  <p>{details.subtitle}</p>
                  <span>{requiresEnquiry ? "Confirmation required" : details.badge}</span>
                </div>
                <img src={details.image} alt={details.title} />
              </div>
              <div className="summary-route">
                <span>{quote.request?.pickup}</span>
                <span>{quote.request?.dropoff}</span>
              </div>
              <div className="summary-facts">
                <span>▣ {displayDate(form.pickup_time)}</span>
                <span>◷ {displayTime(form.pickup_time)}</span>
                <span>♙ {form.passengers || 0} Passengers</span>
                <span>▣ {form.bags || 0} Large bags</span>
              </div>
              {publicFareVisible ? (
                <div className="summary-prices">
                  <p><span>Base fare</span><strong>{money(baseFare)}</strong></p>
                  <p><span>Extras</span><strong>{money(extrasTotal)}</strong></p>
                  <p className="total"><span>Total</span><strong>{money(estimatedTotal)}</strong></p>
                </div>
              ) : (
                <div className="summary-prices">
                  <p className="total"><span>Personalised quote</span><strong>Confirmed by our team</strong></p>
                </div>
              )}
              <div className="price-includes">
                <strong>Price includes</strong>
                <span>✓ Driver</span>
                <span>✓ Fuel</span>
                <span>✓ Airport charges</span>
                <span>✓ All taxes & VAT</span>
                <span>✓ 60-min free waiting time</span>
              </div>
              <button className="premium-booking-submit" type="button" disabled={submitting} onClick={handleSubmit}>
                {submitting ? "Submitting..." : ctaLabel} ▣
              </button>
              <button className="premium-booking-back" type="button" onClick={handleBackToOptions}>← Back to options</button>
            </div>
          </aside>
        </div>

        <footer className="premium-booking-trust">
          <span><b>♢</b><strong>Gov UK Registered Operator</strong><small>Licensed & insured</small></span>
          <span><b>♙</b><strong>10,000+ Successful Journeys</strong><small>Across the UK</small></span>
          <span><b>★</b><strong>Rated 4.9/5 from 2,150+ reviews</strong><small>Google</small></span>
          <span><b>☎</b><strong>24/7 Support</strong><small>Always here to help</small></span>
        </footer>
      </section>
    </main>
  );
};

const premiumBookingCss = `
  .premium-booking-mobile-submit { display: none; }
  .premium-booking-page {
    min-height: 100vh;
    background: linear-gradient(180deg, #f8fbff 0%, #f4f7fb 42%, #ffffff 100%);
    color: #06183a;
    font-family: Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  }
  .premium-booking-shell {
    width: min(1480px, calc(100% - 32px));
    margin: 0 auto;
    padding: 24px 0 54px;
  }
  .premium-booking-progress {
    display: grid;
    grid-template-columns: repeat(5, minmax(120px, 1fr)) 230px;
    gap: 14px;
    align-items: center;
    padding: 18px 0 28px;
    border-bottom: 1px solid #dce6f3;
  }
  .premium-booking-progress span,
  .secure-booking {
    display: flex;
    align-items: center;
    gap: 10px;
    color: #546987;
    font-weight: 800;
    font-size: 14px;
  }
  .premium-booking-progress span b,
  .secure-booking b {
    display: grid;
    place-items: center;
    width: 28px;
    height: 28px;
    border-radius: 999px;
    border: 2px solid #b8c7dc;
    color: #376df6;
    background: #fff;
  }
  .premium-booking-progress span.active {
    color: #06183a;
  }
  .premium-booking-progress span.active b {
    background: #3b78ff;
    color: #fff;
    border-color: #3b78ff;
  }
  .secure-booking {
    justify-content: flex-end;
  }
  .secure-booking b {
    border: 0;
    color: #109b55;
    background: #e7f8ed;
  }
  .secure-booking small,
  .premium-booking-trust small {
    display: block;
    color: #637694;
    font-weight: 600;
    margin-top: 2px;
  }
  .premium-booking-heading {
    padding: 28px 0 18px;
  }
  .premium-booking-heading p {
    margin: 0 0 8px;
    color: #1f6dff;
    font-size: 12px;
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: 0;
  }
  .premium-booking-heading h1 {
    margin: 0 0 10px;
    font-size: clamp(34px, 4vw, 54px);
    line-height: 1.02;
    letter-spacing: 0;
  }
  .premium-booking-heading > strong {
    display: block;
    color: #344966;
    font-size: 22px;
    margin-bottom: 18px;
  }
  .premium-booking-pills {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
  }
  .premium-booking-pills span {
    background: #fff;
    border: 1px solid #dce5f2;
    border-radius: 6px;
    padding: 11px 16px;
    box-shadow: 0 6px 18px rgba(12, 39, 82, 0.06);
    font-weight: 800;
  }
  .premium-booking-grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(360px, 480px);
    gap: 26px;
    align-items: start;
  }
  .premium-booking-card,
  .premium-booking-summary,
  .premium-booking-trust,
  .premium-booking-success,
  .premium-booking-error,
  .premium-booking-loading {
    background: #fff;
    border: 1px solid #dbe5f1;
    border-radius: 14px;
    box-shadow: 0 18px 42px rgba(7, 28, 58, 0.09);
  }
  .premium-booking-form {
    padding: 22px;
  }
  .premium-booking-form section + section {
    margin-top: 26px;
  }
  .premium-booking-form h2 {
    margin: 0 0 18px;
    font-size: 22px;
  }
  .booking-field-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px;
  }
  .booking-field-grid label,
  .wide-field {
    display: flex;
    flex-direction: column;
    gap: 8px;
    font-weight: 800;
    color: #243856;
  }
  .wide-field {
    margin-top: 16px;
  }
  .booking-field-grid label span,
  .wide-field span {
    font-size: 13px;
  }
  .premium-booking-form input,
  .premium-booking-form textarea {
    width: 100%;
    border: 1px solid #cfdbea;
    border-radius: 8px;
    padding: 14px 15px;
    min-height: 48px;
    font: inherit;
    font-weight: 700;
    color: #10223e;
    background: #fff;
  }
  .premium-booking-form textarea {
    min-height: 96px;
    resize: vertical;
  }
  .premium-extra-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 14px;
  }
  .premium-extra-grid label {
    min-height: 92px;
    display: flex;
    gap: 12px;
    align-items: center;
    border: 1px solid #d8e3f0;
    border-radius: 8px;
    padding: 14px;
    cursor: pointer;
  }
  .premium-extra-grid label.disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
  .premium-extra-grid strong,
  .premium-extra-grid small {
    display: block;
  }
  .premium-extra-grid small {
    margin-top: 4px;
    color: #465b7a;
    font-weight: 800;
  }
  .discount-row {
    display: grid;
    grid-template-columns: 1fr 160px;
    gap: 10px;
    margin-top: 16px;
  }
  .discount-row button {
    border: 0;
    border-radius: 8px;
    font-weight: 900;
    cursor: pointer;
    background: #e8edf5;
    color: #06183a;
  }
  .no-charge-note {
    display: flex;
    gap: 12px;
    align-items: center;
    margin-top: 18px;
    padding: 15px;
    background: #edfbf3;
    border-radius: 8px;
    color: #0b6a3f;
  }
  .no-charge-note small {
    display: block;
    color: #214163;
    margin-top: 3px;
  }
  .premium-booking-message {
    margin-top: 16px;
    padding: 12px 14px;
    border-radius: 8px;
    background: #fff1f2;
    color: #b4233a;
    font-weight: 800;
  }
  .premium-booking-summary {
    position: sticky;
    top: 18px;
    overflow: hidden;
  }
  .summary-title {
    padding: 18px 22px;
    background: #06183a;
    color: #fff;
    font-size: 22px;
    font-weight: 900;
  }
  .summary-body {
    padding: 22px;
  }
  .summary-vehicle {
    display: grid;
    grid-template-columns: 1fr 150px;
    gap: 14px;
    align-items: center;
  }
  .summary-vehicle h2 {
    margin: 0 0 5px;
    font-size: 19px;
  }
  .summary-vehicle p {
    margin: 0 0 8px;
    color: #536782;
    font-weight: 700;
  }
  .summary-vehicle span {
    display: inline-block;
    padding: 5px 9px;
    border-radius: 999px;
    background: #e9f8ee;
    color: #0b8c4b;
    font-size: 12px;
    font-weight: 900;
  }
  .summary-vehicle img {
    width: 150px;
    height: 100px;
    object-fit: contain;
  }
  .summary-route {
    display: grid;
    gap: 12px;
    margin: 18px 0;
    padding-left: 18px;
    border-left: 3px solid #2f6fff;
    color: #243856;
    font-weight: 800;
  }
  .summary-facts {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
    border-top: 1px solid #dbe5f1;
    border-bottom: 1px solid #dbe5f1;
    padding: 16px 0;
    color: #4a5f7e;
    font-weight: 800;
  }
  .summary-prices {
    padding: 16px 0;
    border-bottom: 1px solid #dbe5f1;
  }
  .summary-prices p {
    display: flex;
    justify-content: space-between;
    margin: 0 0 10px;
    font-weight: 800;
  }
  .summary-prices .total {
    margin-top: 15px;
    font-size: 18px;
  }
  .summary-prices .total strong {
    font-size: 30px;
  }
  .price-includes {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 8px 16px;
    padding: 15px;
    background: #eaf8f0;
    border-radius: 8px;
    color: #196c43;
    font-weight: 800;
  }
  .price-includes strong {
    grid-column: 1 / -1;
  }
  .premium-booking-submit {
    width: 100%;
    min-height: 54px;
    margin-top: 18px;
    border: 0;
    border-radius: 8px;
    background: linear-gradient(180deg, #ffd21f, #ffb400);
    color: #06183a;
    font-size: 17px;
    font-weight: 950;
    cursor: pointer;
    box-shadow: 0 10px 20px rgba(255, 185, 0, 0.24);
  }
  .premium-booking-submit:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }
  .premium-booking-back {
    display: block;
    margin: 16px auto 0;
    border: 0;
    background: transparent;
    color: #243856;
    font-weight: 900;
    cursor: pointer;
  }
  .premium-booking-trust {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 0;
    margin-top: 24px;
    padding: 18px;
  }
  .premium-booking-trust span {
    display: flex;
    gap: 12px;
    align-items: center;
    padding: 0 22px;
    border-right: 1px solid #dbe5f1;
  }
  .premium-booking-trust span:last-child {
    border-right: 0;
  }
  .premium-booking-trust b {
    color: #146ef5;
    font-size: 25px;
  }
  .premium-booking-success,
  .premium-booking-error,
  .premium-booking-loading {
    width: min(520px, calc(100% - 32px));
    margin: 80px auto;
    padding: 28px;
    text-align: center;
  }
  .premium-booking-success .success-mark {
    width: 54px;
    height: 54px;
    display: grid;
    place-items: center;
    margin: 0 auto 14px;
    border-radius: 999px;
    background: #e8f8ee;
    color: #0b8c4b;
    font-size: 28px;
    font-weight: 900;
  }
  .premium-booking-success p {
    margin: 0 0 8px;
    color: #1f6dff;
    font-size: 12px;
    font-weight: 900;
    text-transform: uppercase;
  }
  .premium-booking-success h1,
  .premium-booking-error h1 {
    margin: 0 0 12px;
  }
  .premium-booking-success span,
  .premium-booking-success strong,
  .premium-booking-success small {
    display: block;
    margin-top: 8px;
  }
  .premium-booking-success button,
  .premium-booking-error button {
    margin-top: 20px;
    border: 0;
    border-radius: 8px;
    padding: 13px 18px;
    background: #ffc400;
    color: #06183a;
    font-weight: 900;
    cursor: pointer;
  }
  @media (max-width: 1100px) {
    .premium-booking-progress {
      grid-template-columns: repeat(3, 1fr);
    }
    .secure-booking {
      justify-content: flex-start;
    }
    .premium-booking-grid {
      grid-template-columns: 1fr;
    }
    .premium-booking-summary {
      position: static;
    }
    .premium-extra-grid,
    .premium-booking-trust {
      grid-template-columns: repeat(2, 1fr);
    }
    .premium-booking-trust span {
      border-right: 0;
      border-bottom: 1px solid #dbe5f1;
      padding: 14px;
    }
  }
  @media (max-width: 720px) {
    .premium-booking-mobile-submit {
      display: grid;
      gap: 8px;
      padding: 14px;
      border: 1px solid #a7f3d0;
      border-radius: 10px;
      background: #ecfdf5;
    }
    .premium-booking-mobile-submit strong { font-size: 22px; }
    .premium-booking-mobile-submit small { color: #31516a; font-weight: 700; }
    .premium-booking-ccg .premium-booking-summary .premium-booking-submit { display: none; }
    .premium-booking-shell {
      width: min(100% - 20px, 560px);
      padding-top: 10px;
    }
    .premium-booking-progress {
      grid-template-columns: 1fr;
      gap: 8px;
    }
    .premium-booking-progress span:not(.active) {
      display: none;
    }
    .premium-booking-heading h1 {
      font-size: 32px;
    }
    .premium-booking-heading > strong {
      font-size: 17px;
    }
    .booking-field-grid,
    .premium-extra-grid,
    .discount-row,
    .summary-vehicle,
    .summary-facts,
    .price-includes,
    .premium-booking-trust {
      grid-template-columns: 1fr;
    }
    .summary-vehicle img {
      width: 100%;
      max-width: 240px;
      height: 135px;
    }
    .premium-booking-form,
    .summary-body {
      padding: 16px;
    }
  }
`;

export default PremiumBooking;
